import React, { useState, useContext, useReducer, useRef, useLayoutEffect } from 'react';
import { Box, ThemeProvider, Flex, useThemeUI } from 'theme-ui';

var isMergeableObject = function isMergeableObject(value) {
  return isNonNullObject(value) && !isSpecial(value);
};

function isNonNullObject(value) {
  return !!value && typeof value === 'object';
}

function isSpecial(value) {
  var stringValue = Object.prototype.toString.call(value);
  return stringValue === '[object RegExp]' || stringValue === '[object Date]' || isReactElement(value);
} // see https://github.com/facebook/react/blob/b5ac963fb791d1298e7f396236383bc955f916c1/src/isomorphic/classic/element/ReactElement.js#L21-L25


var canUseSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;

function isReactElement(value) {
  return value.$$typeof === REACT_ELEMENT_TYPE;
}

function emptyTarget(val) {
  return Array.isArray(val) ? [] : {};
}

function cloneUnlessOtherwiseSpecified(value, options) {
  return options.clone !== false && options.isMergeableObject(value) ? deepmerge(emptyTarget(value), value, options) : value;
}

function defaultArrayMerge(target, source, options) {
  return target.concat(source).map(function (element) {
    return cloneUnlessOtherwiseSpecified(element, options);
  });
}

function getMergeFunction(key, options) {
  if (!options.customMerge) {
    return deepmerge;
  }

  var customMerge = options.customMerge(key);
  return typeof customMerge === 'function' ? customMerge : deepmerge;
}

function getEnumerableOwnPropertySymbols(target) {
  return Object.getOwnPropertySymbols ? Object.getOwnPropertySymbols(target).filter(function (symbol) {
    return target.propertyIsEnumerable(symbol);
  }) : [];
}

function getKeys(target) {
  return Object.keys(target).concat(getEnumerableOwnPropertySymbols(target));
}

function propertyIsOnObject(object, property) {
  try {
    return property in object;
  } catch (_) {
    return false;
  }
} // Protects from prototype poisoning and unexpected merging up the prototype chain.


function propertyIsUnsafe(target, key) {
  return propertyIsOnObject(target, key) // Properties are safe to merge if they don't exist in the target yet,
  && !(Object.hasOwnProperty.call(target, key) // unsafe if they exist up the prototype chain,
  && Object.propertyIsEnumerable.call(target, key)); // and also unsafe if they're nonenumerable.
}

function mergeObject(target, source, options) {
  var destination = {};

  if (options.isMergeableObject(target)) {
    getKeys(target).forEach(function (key) {
      destination[key] = cloneUnlessOtherwiseSpecified(target[key], options);
    });
  }

  getKeys(source).forEach(function (key) {
    if (propertyIsUnsafe(target, key)) {
      return;
    }

    if (propertyIsOnObject(target, key) && options.isMergeableObject(source[key])) {
      destination[key] = getMergeFunction(key, options)(target[key], source[key], options);
    } else {
      destination[key] = cloneUnlessOtherwiseSpecified(source[key], options);
    }
  });
  return destination;
}

function deepmerge(target, source, options) {
  options = options || {};
  options.arrayMerge = options.arrayMerge || defaultArrayMerge;
  options.isMergeableObject = options.isMergeableObject || isMergeableObject; // cloneUnlessOtherwiseSpecified is added to `options` so that custom arrayMerge()
  // implementations can use it. The caller may not replace it.

  options.cloneUnlessOtherwiseSpecified = cloneUnlessOtherwiseSpecified;
  var sourceIsArray = Array.isArray(source);
  var targetIsArray = Array.isArray(target);
  var sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;

  if (!sourceAndTargetTypesMatch) {
    return cloneUnlessOtherwiseSpecified(source, options);
  } else if (sourceIsArray) {
    return options.arrayMerge(target, source, options);
  } else {
    return mergeObject(target, source, options);
  }
}

deepmerge.all = function deepmergeAll(array, options) {
  if (!Array.isArray(array)) {
    throw new Error('first argument should be an array');
  }

  return array.reduce(function (prev, next) {
    return deepmerge(prev, next, options);
  }, {});
};

var deepmerge_1 = deepmerge;
var cjs = deepmerge_1;
var ExtendContext = React.createContext();
var ExtendUpdateContext = React.createContext(); // Provider

var ExtensionProvider = function ExtensionProvider(ref) {
  var children = ref.children;
  var ref$1 = useState({
    extendedOptions: {},
    extendedTheme: {},
    themeKeys: []
  });
  var state = ref$1[0];
  var set = ref$1[1];
  return React.createElement(ExtendContext.Provider, {
    value: state
  }, React.createElement(ExtendUpdateContext.Provider, {
    value: set
  }, children));
}; // Usage Hook


function useExtensions() {
  var state = useContext(ExtendContext);
  var setState = useContext(ExtendUpdateContext);

  if (state === undefined) {
    throw new Error('useExtensions must be used within the Maker UI framework');
  }

  return [state, setState];
}

var defaultOptions = {
  navigation: 'basic',
  layout: 'content',
  topbar: {
    maxWidth: 1260,
    hideOnMobile: false,
    breakIndex: 0
  },
  header: {
    maxWidth: 1460,
    sticky: false,
    stickyMobile: false,
    stickyScroll: false,
    scroll: {
      toggleClass: false,
      scrollTop: 200,
      className: 'sticky'
    },
    colorToggle: true,
    hideColorToggleOnMobile: false,
    hideWidgetsOnMobile: true,
    dropdown: {
      caret: true,
      transition: 'scale'
    },
    breakIndex: 0
  },
  mobileMenu: {
    width: '60vw',
    // note: can be responsive array
    transition: 'slideLeft',
    visibleOnDesktop: false,
    defaultCloseButton: false,
    closeOnBlur: true,
    closeOnRouteChange: false
  },
  sideNav: {
    width: 300,
    isHeader: false,
    // TODO
    isPrimaryMobileNav: false,
    floatingToggle: true,
    closeOnBlur: true,
    closeOnRouteChange: true,
    breakIndex: 0
  },
  content: {
    maxWidth: 1020,
    maxWidthSection: 1020,
    sidebarGap: 30,
    breakIndex: 0
  },
  sidebar: {
    width: 300
  },
  footer: {
    maxWidth: 1020
  },
  a11y: {
    skiplinks: true
  }
};
var OptionContext = React.createContext();
var OptionUpdateContext = React.createContext(); // Provider

var OptionProvider = function OptionProvider(ref) {
  var options = ref.options;
  if (options === void 0) options = {};
  var children = ref.children;
  var ref$1 = useState(cjs(defaultOptions, options));
  var state = ref$1[0];
  var dispatch = ref$1[1];
  return React.createElement(OptionContext.Provider, {
    value: state
  }, React.createElement(OptionUpdateContext.Provider, {
    value: dispatch
  }, children));
}; // Usage Hooks


function useOptions() {
  var options = useContext(OptionContext);

  if (options === undefined) {
    throw new Error('useOptions must be used within an Maker UI Layout component');
  }

  return options;
}

function useOptionUpdater() {
  var dispatch = useContext(OptionUpdateContext);

  if (dispatch === undefined) {
    throw new Error('useOptionsUpdater must be used within an Maker UI Layout component');
  }

  function setOptions(options) {
    dispatch(function (state) {
      return cjs(state, options);
    });
  }

  return setOptions;
}

function useLayout() {
  var ref = useContext(OptionContext);
  var layout = ref.layout;
  var dispatch = useContext(OptionUpdateContext);

  if (layout === undefined) {
    throw new Error('useLayout must be used within an Maker UI layout');
  }

  function setLayout(newLayout) {
    dispatch(function (state) {
      return Object.assign({}, state, {
        layout: newLayout
      });
    });
  }

  return [layout, setLayout];
}

var ActionContext = React.createContext();
var ActionUpdateContext = React.createContext();

function reducer(state, action) {
  switch (action.type) {
    case 'menu':
      {
        return Object.assign({}, state, {
          menuActive: !state.menuActive
        });
      }

    case 'sideNav':
      {
        return Object.assign({}, state, {
          sideNavActive: !state.sideNavActive
        });
      }

    default:
      {
        throw new Error("Unhandled action type: " + action.type);
      }
  }
} // Provider


var ActionProvider = function ActionProvider(ref) {
  var children = ref.children;
  var ref$1 = useReducer(reducer, {
    menuActive: false,
    sideNavActive: false
  });
  var state = ref$1[0];
  var dispatch = ref$1[1];
  return React.createElement(ActionContext.Provider, {
    value: state
  }, React.createElement(ActionUpdateContext.Provider, {
    value: dispatch
  }, children));
}; // Usage Hooks


function useMenu() {
  var ref = useContext(ActionContext);
  var menuActive = ref.menuActive;
  var dispatch = useContext(ActionUpdateContext);

  if (typeof menuActive === undefined) {
    throw new Error('useMenu must be used within an Maker UI layout');
  }

  function toggleMenu() {
    dispatch({
      type: 'menu'
    });
  }

  return [menuActive, toggleMenu];
}

function useSideNav() {
  var ref = useContext(ActionContext);
  var sideNavActive = ref.sideNavActive;
  var dispatch = useContext(ActionUpdateContext);

  if (typeof sideNavActive === undefined) {
    throw new Error('useSideNav must be used within an Maker UI layout');
  }

  function setSideNav() {
    dispatch({
      type: 'sideNav'
    });
  }

  return [sideNavActive, setSideNav];
}

var Skiplinks = function Skiplinks() {
  var ref = useOptions();
  var layout = ref.layout;
  var a11y = ref.a11y;
  var links = [{
    id: '#content',
    label: 'Skip to content'
  }, {
    id: '#footer',
    label: 'Skip to footer'
  }];

  if (layout.includes('sidenav')) {
    links.splice(1, 0, {
      id: '#side-nav',
      label: 'Skip to side navigation'
    });
  }

  return a11y.skiplinks ? React.createElement(Box, {
    as: "ul",
    __css: {
      listStyle: 'none',
      position: 'relative',
      zIndex: 1000,
      p: 0,
      a: {
        bg: 'bg_header',
        display: 'block',
        position: 'absolute',
        fontFamily: 'body',
        left: -9999,
        p: '1em',
        '&:focus': {
          left: 0
        }
      }
    }
  }, links.map(function (ref) {
    var id = ref.id;
    var label = ref.label;
    return React.createElement('li', {
      key: id
    }, React.createElement('a', {
      href: id
    }, label));
  })) : null;
};

function generateId(length) {
  if (length === void 0) length = 5;
  var result = '';
  var chars = 'abcdefghijklmnopqrstuv1234567890';
  var charLength = chars.length;

  for (var i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * charLength));
  }

  return result;
}

function setBreakpoint(index, arr) {
  var i = 0;

  while (i < index) {
    arr.unshift(null);
    i++;
  }

  return arr;
}

function validate(obj) {
  return obj !== undefined && typeof obj === 'object' ? obj : {};
}

var content = {
  display: 'block',
  maxWidth: 'maxWidth_content',
  mx: 'auto'
};
var sidebar = {
  base_sidebar: {
    display: 'grid',
    gridGap: function gridGap(t) {
      return t.gap.gap_content;
    },
    maxWidth: 'maxWidth_content',
    mx: 'auto'
  },
  'sidebar-content': {
    variant: 'eui_layout.base_sidebar',
    '#primary-sidebar': {
      gridRow: [2, 'auto']
    }
  },
  'content-sidebar': {
    variant: 'eui_layout.base_sidebar'
  }
};
var sideNav = {
  base_sidenav: {
    display: 'flex',
    '#content': {
      maxWidth: 'maxWidth_content',
      mx: 'auto'
    }
  },
  'sidenav-content': {
    variant: 'eui_layout.base_sidenav',
    '#side-nav': {
      left: 0
    },
    '#toggle-sidenav': {
      right: 30
    }
  },
  'content-sidenav': {
    variant: 'eui_layout.base_sidenav',
    '#side-nav': {
      right: 0
    },
    '#toggle-sidenav': {
      left: 30
    }
  }
};
var layouts = {
  eui_layout: Object.assign({}, {
    content: content,
    'full-width': {
      display: 'block',
      maxWidth: '100%'
    }
  }, sidebar, sideNav)
};
var headers = {
  eui_header: {
    base: {
      position: 'relative',
      m: '0 auto',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    default: {
      variant: 'eui_header.base',
      justifyContent: 'space-between'
    },
    center: {
      variant: 'eui_header.base',
      justifyContent: ['space-between', 'center']
    },
    columns: {
      variant: 'eui_header.base',
      '.col-1': {
        alignItems: 'center',
        width: ['25%', '33%'],
        button: {
          m: 0
        }
      },
      '.col-2': {
        width: ['50%', '34%'],
        justifyContent: 'center',
        alignItems: 'center'
      },
      '.col-3': {
        width: ['25%', '33%'],
        alignItems: 'center',
        justifyContent: 'flex-end'
      }
    }
  }
};
var base = {
  opacity: 1,
  visibility: 'visible'
};
var fade = {
  transition: 'all ease .3s'
};

var dropdownStyles = function dropdownStyles(type) {
  switch (type) {
    case 'fade':
      return Object.assign({}, fade, {
        active: base
      });

    case 'fadeInDown':
      return Object.assign({}, {
        transform: 'translateY(-10px)'
      }, fade, {
        active: Object.assign({}, base, {
          transform: 'translateY(0)'
        })
      });

    case 'fadeInUp':
      return Object.assign({}, {
        transform: 'translateY(-10px)'
      }, fade, {
        active: Object.assign({}, base, {
          transform: 'translateY(0)'
        })
      });

    case 'scale':
      {
        return Object.assign({}, {
          transform: 'scale(1, 0)',
          transformOrigin: '0 0',
          transition: 'transform ease-in-out .25s'
        }, base, {
          li: {
            opacity: 0,
            transition: 'opacity .03s ease-in-out'
          },
          active: {
            transform: 'scale(1, 1)',
            li: {
              opacity: 1,
              transition: 'opacity ease-in-out .2s .2s'
            }
          }
        });
      }

    default:
      return {
        active: base
      };
  }
};

var submenu = function submenu(type) {
  return {
    eui_submenu: Object.assign({}, dropdownStyles(type))
  };
};

var themeMap = function themeMap(theme, extendedTheme, options) {
  var o = options === undefined ? defaultOptions : cjs(defaultOptions, options);
  var mappedOptions = Object.assign({}, {
    colors: {
      bg_topbar: '#355cac',
      bg_header: '#000',
      bg_mobileMenu: 'rgba(0, 0, 0, 0.9)',
      bg_sideNav: '#333',
      bg_footer: '#fff'
    },
    fontWeights: {
      body: 400,
      heading: 700,
      bold: 700
    },
    fontSizes: [12, 14, 16, 20, 24, 32, 48, 64],
    space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
    breakpoints: ['750px', '960px', '1240px'],
    sizes: {
      maxWidth_header: o.header.maxWidth,
      maxWidth_topbar: o.topbar.maxWidth,
      maxWidth_content: o.content.maxWidth,
      maxWidth_section: o.content.maxWidthSection,
      maxWidth_footer: o.footer.maxWidth,
      width_mobileMenu: o.mobileMenu.width,
      width_sidebar: o.sidebar.width,
      width_sideNav: o.sideNav.width
    },
    gap: {
      gap_content: o.content.sidebarGap
    }
  }, layouts, headers, submenu(o.header.dropdown.transition));
  return cjs.all([mappedOptions, validate(extendedTheme), validate(theme)], {
    arrayMerge: function arrayMerge(target, source, options) {
      return source;
    }
  });
};

var Layout = function Layout(props) {
  return React.createElement(ExtensionProvider, null, React.createElement(Root, props));
};

var Root = function Root(ref) {
  var theme = ref.theme;
  var options = ref.options;
  var components = ref.components;
  var children = ref.children;
  var ref$1 = useExtensions();
  var ref$1_0 = ref$1[0];
  var extendedOptions = ref$1_0.extendedOptions;
  var extendedTheme = ref$1_0.extendedTheme;
  var allOptions = cjs(extendedOptions, options);
  return React.createElement(ThemeProvider, {
    theme: themeMap(theme, extendedTheme, allOptions),
    components: components
  }, React.createElement(OptionProvider, {
    options: allOptions
  }, React.createElement(ActionProvider, null, React.createElement(Skiplinks, null), children)));
};

function objectWithoutProperties(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Topbar = React.forwardRef(function (ref$1, ref) {
  var bg = ref$1.bg;
  if (bg === void 0) bg = 'bg_topbar';
  var maxWidth = ref$1.maxWidth;
  if (maxWidth === void 0) maxWidth = 'maxWidth_topbar';
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'topbar';
  var scrollOverflow = ref$1.scrollOverflow;
  if (scrollOverflow === void 0) scrollOverflow = false;
  var rest = objectWithoutProperties(ref$1, ["bg", "maxWidth", "variant", "scrollOverflow"]);
  var props = rest;
  var ref$2 = useOptions();
  var topbar = ref$2.topbar;
  return React.createElement(Box, {
    ref: ref,
    as: "aside",
    variant: variant,
    role: "complementary",
    bg: bg,
    sx: {
      display: topbar.hideOnMobile ? setBreakpoint(topbar.breakIndex, ['none', 'block']) : 'block'
    }
  }, React.createElement(Flex, Object.assign({}, props, {
    __css: {
      mx: 'auto',
      overflowX: scrollOverflow ? 'scroll' : null,
      whiteSpace: scrollOverflow ? 'nowrap' : null,
      maxWidth: maxWidth
    }
  })));
}); // Credit to n8tb1t's https://github.com/n8tb1t/use-scroll-position

var isBrowser = typeof window !== 'undefined';

function getScrollPosition() {
  if (!isBrowser) {
    return {
      x: 0,
      y: 0
    };
  }

  var position = document.body.getBoundingClientRect();
  return Math.abs(position.top);
}

function useScrollPosition(effect, wait) {
  var position = useRef(getScrollPosition());
  useLayoutEffect(function () {
    if (!isBrowser) {
      return;
    }

    var throttleTimeout = null;

    var callBack = function callBack() {
      var currPos = getScrollPosition();
      effect({
        prevPos: position.current,
        currPos: currPos
      });
      position.current = currPos;
      throttleTimeout = null;
    };

    var handleScroll = function handleScroll() {
      if (wait) {
        if (throttleTimeout === null) {
          throttleTimeout = setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return function () {
      return window.removeEventListener('scroll', handleScroll);
    };
  }, [effect, wait]);
}

function objectWithoutProperties$1(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Header = React.forwardRef(function (props, ref) {
  var ref$1 = useOptions();
  var header = ref$1.header;
  var ref$2 = useState(null);
  var scrollClass = ref$2[0];
  var setScrollClass = ref$2[1];
  var ref$3 = useState(true);
  var show = ref$3[0];
  var setShow = ref$3[1];
  var bg = props.bg;
  if (bg === void 0) bg = 'bg_header';
  var variant = props.variant;
  if (variant === void 0) variant = 'header';
  var sticky = props.sticky;
  if (sticky === void 0) sticky = header.sticky;
  var stickyMobile = props.stickyMobile;
  if (stickyMobile === void 0) stickyMobile = header.stickyMobile;
  var stickyScroll = props.stickyScroll;
  if (stickyScroll === void 0) stickyScroll = header.stickyScroll;
  var rest$1 = objectWithoutProperties$1(props, ["bg", "variant", "sticky", "stickyMobile", "stickyScroll"]);
  var rest = rest$1;

  if (stickyScroll) {
    useScrollPosition(function (ref) {
      var prevPos = ref.prevPos;
      var currPos = ref.currPos;
      var isDownScroll = currPos > prevPos;
      var aboveLimit = currPos > 500;

      if (!aboveLimit && !show) {
        setShow(true);
      }

      if (aboveLimit && isDownScroll && show) {
        setShow(false);
      }

      if (aboveLimit && !isDownScroll && !show) {
        setShow(true);
      }
    }, 250);
  }

  if (header.scroll.toggleClass) {
    var ref$4 = header.scroll;
    var scrollTop = ref$4.scrollTop;
    var className = ref$4.className;
    useScrollPosition(function (ref) {
      var currPos = ref.currPos;
      var isActive = currPos > scrollTop ? className : null;

      if (isActive !== scrollClass) {
        setScrollClass(isActive);
      }
    }, 0);
  }

  var stickyPartial = stickyScroll ? {
    position: 'sticky',
    top: 0,
    transform: show ? 'none' : 'translateY(-100%)',
    transition: "transform .3s " + (show ? "ease-in" : "ease-out")
  } : sticky ? {
    top: 0,
    position: stickyMobile ? 'sticky' : setBreakpoint(header.breakIndex, ['initial', 'sticky'])
  } : !sticky && stickyMobile ? {
    top: 0,
    position: setBreakpoint(header.breakIndex, ['sticky', 'initial'])
  } : {
    position: 'relative'
  };
  return React.createElement(Box, Object.assign({}, {
    ref: ref,
    as: "header",
    id: "site-header",
    className: scrollClass,
    role: "banner",
    variant: variant,
    bg: bg
  }, rest, {
    __css: Object.assign({}, {
      zIndex: 100
    }, stickyPartial)
  }));
});

var ColorButton = function ColorButton(ref) {
  var custom = ref.custom;
  var ref$1 = useThemeUI();
  var theme = ref$1.theme;
  var colorMode = ref$1.colorMode;
  var setColorMode = ref$1.setColorMode;
  var ref$2 = useOptions();
  var header = ref$2.header;
  var modes = theme.colors.modes ? [theme.initialColorModeName].concat(Object.keys(theme.colors.modes)) : [theme.initialColorModeName];

  var cycleMode = function cycleMode() {
    var i = modes.indexOf(colorMode);
    var next = modes[(i + 1) % modes.length];
    setColorMode(next);
  };

  return modes.length === 1 && header.colorToggle ? null : React.createElement(Box, {
    as: "button",
    title: "Color Mode",
    className: "color-toggle",
    'aria-label': "Toggle Color Mode",
    variant: "header.colorButton",
    onClick: cycleMode,
    sx: {
      display: header.hideColorToggleOnMobile ? setBreakpoint(header.breakIndex, ['none', 'block']) : 'block'
    }
  }, custom || colorMode);
};

var Overlay = function Overlay(ref) {
  var show = ref.show;
  var toggle = ref.toggle;
  var type = ref.type;
  var bp = ref.bp;
  var visibility = show ? 'visible' : 'hidden';
  var opacity = show ? 1 : 0;
  var partial = type === 'sideNav' ? {
    visibility: setBreakpoint(bp, [visibility, 'hidden']),
    opacity: setBreakpoint(bp, [opacity, 0])
  } : {
    visibility: visibility,
    opacity: opacity
  };
  return React.createElement(Box, {
    className: "menu-overlay",
    role: "presentation",
    onClick: toggle,
    sx: Object.assign({}, {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bg: 'rgba(0, 0, 0, 0.15)',
      zIndex: 100
    }, partial, {
      willChange: 'opacity',
      transition: 'all ease .4s'
    })
  });
};

var getAttributes = function getAttributes(isHeader, set) {
  return isHeader ? {
    onFocus: function onFocus(e) {
      return set(true);
    },
    onBlur: function onBlur(e) {
      return set(false);
    },
    onClick: function onClick(e) {
      return set(false);
    }
  } : null;
};

var Dropdown = function Dropdown(ref) {
  var submenu = ref.submenu;
  var active = ref.active;
  var set = ref.set;
  var isHeader = ref.isHeader;
  var menuControls = ref.menuControls;
  var pathname = ref.pathname;
  return React.createElement(React.Fragment, null, !isHeader && React.createElement(Box, {
    as: "button",
    title: "Expand Section",
    className: "submenu-toggle",
    'aria-expanded': active ? 'true' : 'false',
    'aria-label': "Expand Section",
    onClick: function onClick() {
      return set(!active);
    },
    sx: {
      border: 'none',
      bg: 'transparent'
    }
  }, React.createElement(Box, {
    as: "svg",
    viewBox: "0 0 16 16",
    width: "12",
    height: "12",
    sx: {
      transition: 'transform ease .2s',
      transformOrigin: '50% 55%',
      transform: active ? 'rotate(180deg)' : null
    }
  }, React.createElement('path', {
    stroke: "currentcolor",
    strokeWidth: "2",
    fill: "none",
    d: "M14 6 L8 12 L2 6"
  }))), isHeader || !isHeader && active ? React.createElement(Box, {
    as: "ul",
    variant: isHeader ? 'header.submenu' : 'accordion',
    className: "sub-menu " + (active ? 'active' : ''),
    sx: isHeader ? {
      position: 'absolute',
      display: 'inline-block',
      width: 'max-content',
      top: '99%',
      left: 0,
      opacity: 0,
      visibility: 'hidden',
      m: 0,
      p: 0,
      zIndex: 1,
      listStyle: 'none',
      variant: 'eui_submenu',
      '&.active': {
        variant: 'eui_submenu.active'
      }
    } : {}
  }, submenu.map(function (ref, index) {
    var label = ref.label;
    var path = ref.path;
    var newTab = ref.newTab;
    var classes = ref.classes;
    if (classes === void 0) classes = '';
    var icon = ref.icon;
    return React.createElement(Box, {
      as: "li",
      key: index,
      className: "menu-item " + classes
    }, React.createElement('a', Object.assign({}, {
      href: path,
      className: pathname === path ? 'current' : undefined,
      target: newTab && '_blank',
      rel: newTab && 'noopener noreferrer',
      'aria-label': icon ? label : undefined,
      'aria-current': pathname === path ? 'page' : undefined
    }, getAttributes(isHeader, set), menuControls), icon && React.createElement('span', {
      className: "submenu-icon"
    }, icon), React.createElement('span', {
      className: "submenu-text"
    }, label)));
  })) : null);
};

var getAttributes$1 = function getAttributes$1(isHeader, submenu, show, set) {
  return isHeader ? {
    onFocus: submenu && function (e) {
      return set(true);
    },
    onBlur: submenu && function (e) {
      return set(false);
    },
    'aria-expanded': submenu ? show ? 'true' : 'false' : null
  } : null;
};

var MenuItem = function MenuItem(ref) {
  var ref_data = ref.data;
  var label = ref_data.label;
  var path = ref_data.path;
  var newTab = ref_data.newTab;
  var submenu = ref_data.submenu;
  var classes = ref_data.classes;
  if (classes === void 0) classes = '';
  var icon = ref_data.icon;
  var caret = ref.caret;
  if (caret === void 0) caret = false;
  var menuControls = ref.menuControls;
  var pathname = ref.pathname;
  var isHeader = ref.isHeader;
  if (isHeader === void 0) isHeader = false;
  var ref$1 = useState(false);
  var show = ref$1[0];
  var set = ref$1[1];
  return React.createElement(Box, {
    as: "li",
    className: "menu-item " + classes,
    sx: isHeader ? {
      position: 'relative',
      display: 'inline-flex',
      '&:hover': {
        '.sub-menu': {
          variant: 'eui_submenu.active'
        }
      },
      '.menu-text:after': submenu && caret ? {
        content: '""',
        display: 'inline-block',
        width: 0,
        height: 0,
        ml: '.4em',
        verticalAlign: '.25em',
        borderTop: '.25em solid',
        borderRight: '.25em solid transparent',
        borderLeft: '.25em solid transparent'
      } : null
    } : {}
  }, React.createElement('a', Object.assign({}, {
    href: path,
    className: pathname === path ? 'current' : undefined,
    target: newTab && '_blank',
    rel: newTab && 'noopener noreferrer',
    'aria-label': icon ? label : null,
    'aria-current': pathname === path ? 'page' : undefined
  }, menuControls, getAttributes$1(isHeader, submenu, show, set)), icon && React.createElement('span', {
    className: "menu-icon"
  }, icon), React.createElement('span', {
    className: "menu-text"
  }, label)), submenu ? React.createElement(Dropdown, {
    submenu: submenu,
    active: show,
    set: set,
    isHeader: isHeader,
    menuControls: menuControls,
    pathname: pathname
  }) : null);
};

var Menu = React.memo(function (ref) {
  var menuItems = ref.menuItems;
  if (menuItems === void 0) menuItems = [];
  var pathname = ref.pathname;
  var ref$1 = useOptions();
  var header = ref$1.header;
  return React.createElement(Box, {
    as: "nav",
    className: "nav-primary",
    sx: {
      display: setBreakpoint(header.breakIndex, ['none', 'flex'])
    }
  }, React.createElement(Box, {
    as: "ul",
    variant: "header.menu",
    className: "menu-primary"
  }, menuItems.map(function (item, index) {
    return React.createElement(MenuItem, {
      key: index,
      data: item,
      caret: header.dropdown.caret,
      pathname: pathname,
      isHeader: true
    });
  })));
});

function objectWithoutProperties$2(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var getAttributes$2 = function getAttributes$2(menu, toggleMenu, sideMenu, toggleSideMenu, sideNavPrimary) {
  return sideNavPrimary ? {
    'aria-expanded': sideMenu ? 'true' : 'false',
    onClick: toggleSideMenu
  } : {
    'aria-expanded': menu ? 'true' : 'false',
    onClick: toggleMenu
  };
};

var MenuButton = function MenuButton(props) {
  var ref = useMenu();
  var menu = ref[0];
  var toggleMenu = ref[1];
  var ref$1 = useSideNav();
  var sideMenu = ref$1[0];
  var toggleSideMenu = ref$1[1];
  var ref$2 = useOptions();
  var mobileMenu = ref$2.mobileMenu;
  var header = ref$2.header;
  var sideNav = ref$2.sideNav;
  var custom = props.custom;
  var visibleOnDesktop = props.visibleOnDesktop;
  if (visibleOnDesktop === void 0) visibleOnDesktop = mobileMenu.visibleOnDesktop;
  var closeIcon = props.closeIcon;
  var rest$1 = objectWithoutProperties$2(props, ["custom", "visibleOnDesktop", "closeIcon"]);
  var rest = rest$1;
  var visibility = visibleOnDesktop ? !sideNav.isPrimaryMobileNav ? {
    display: 'block'
  } : {
    display: 'none'
  } : {
    display: setBreakpoint(header.breakIndex, ['block', 'none'])
  };
  return React.createElement(Box, Object.assign({}, {
    as: "button",
    title: "Menu",
    className: "menu-toggle",
    'aria-label': "Toggle Menu"
  }, getAttributes$2(menu, toggleMenu, sideMenu, toggleSideMenu, sideNav.isPrimaryMobileNav), rest, {
    variant: "header.menuButton",
    __css: Object.assign({}, visibility, {
      m: '0 auto',
      border: 'none',
      background: 'none',
      svg: {
        m: '0 auto'
      }
    })
  }), custom || React.createElement(Box, {
    as: "svg",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    sx: {
      display: 'block',
      margin: 0,
      height: closeIcon ? 35 : 27
    }
  }, closeIcon ? React.createElement('path', {
    d: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
  }) : React.createElement('path', {
    d: "M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"
  })));
};

var WidgetArea = function WidgetArea(props) {
  var ref = useOptions();
  var header = ref.header;
  var custom = props.custom;
  var hideOnMobile = props.hideOnMobile;
  if (hideOnMobile === void 0) hideOnMobile = header.hideWidgetsOnMobile;
  return custom ? React.createElement(Box, {
    variant: "header.widgets",
    className: "widget-area",
    sx: {
      display: hideOnMobile ? setBreakpoint(header.breakIndex, ['none', 'flex']) : 'flex'
    }
  }, custom) : null;
};

function objectWithoutProperties$3(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Logo = function Logo(ref) {
  var children = ref.children;
  var rest = objectWithoutProperties$3(ref, ["children"]);
  var props = rest;
  return React.createElement(Box, Object.assign({}, {
    as: "a",
    href: "/",
    id: "site-logo",
    variant: "header.logo",
    'aria-label': "Home page",
    sx: {
      display: 'flex'
    }
  }, props), children);
};

function objectWithoutProperties$4(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Basic = React.forwardRef(function (ref$1, ref) {
  var logo = ref$1.logo;
  if (logo === void 0) logo = 'Logo';
  var menu = ref$1.menu;
  var widgetArea = ref$1.widgetArea;
  var menuToggle = ref$1.menuToggle;
  var colorToggle = ref$1.colorToggle;
  var bp = ref$1.bp;
  var type = ref$1.type;
  var maxWidth = ref$1.maxWidth;
  if (maxWidth === void 0) maxWidth = 'maxWidth_header';
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'navbar';
  var rest = objectWithoutProperties$4(ref$1, ["logo", "menu", "widgetArea", "menuToggle", "colorToggle", "bp", "type", "maxWidth", "variant"]);
  var props = rest;
  return React.createElement(Flex, Object.assign({}, {
    ref: ref,
    variant: variant
  }, props, {
    __css: {
      variant: 'eui_header.default',
      maxWidth: maxWidth
    }
  }), React.createElement(Logo, null, logo), React.createElement(Flex, {
    sx: {
      alignItems: 'center',
      flex: type === 2 ? 1 : 'initial',
      justifyContent: type === 2 ? setBreakpoint(bp, ['flex-end', 'space-between']) : null
    }
  }, React.createElement(Menu, {
    menuItems: menu
  }), React.createElement(Flex, {
    sx: {
      alignItems: 'center'
    }
  }, React.createElement(WidgetArea, {
    custom: widgetArea
  }), React.createElement(MenuButton, {
    custom: menuToggle
  }), React.createElement(ColorButton, {
    custom: colorToggle
  }))));
});

function objectWithoutProperties$5(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Center = React.forwardRef(function (ref$1, ref) {
  var logo = ref$1.logo;
  if (logo === void 0) logo = 'Logo';
  var menu = ref$1.menu;
  var widgetArea = ref$1.widgetArea;
  var menuToggle = ref$1.menuToggle;
  var colorToggle = ref$1.colorToggle;
  var bp = ref$1.bp;
  var type = ref$1.type;
  var maxWidth = ref$1.maxWidth;
  if (maxWidth === void 0) maxWidth = 'maxWidth_header';
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'navbar';
  var rest = objectWithoutProperties$5(ref$1, ["logo", "menu", "widgetArea", "menuToggle", "colorToggle", "bp", "type", "maxWidth", "variant"]);
  var props = rest;
  var mid = Math.ceil(menu.length / 2);

  var renderBlock = function renderBlock() {
    return React.createElement(Flex, {
      sx: {
        position: setBreakpoint(bp, ['relative', 'absolute']),
        right: 0,
        alignItems: 'center'
      }
    }, React.createElement(WidgetArea, {
      custom: widgetArea
    }), React.createElement(MenuButton, {
      custom: menuToggle
    }), React.createElement(ColorButton, {
      custom: colorToggle
    }));
  };

  return React.createElement(Flex, Object.assign({}, {
    ref: ref,
    variant: variant
  }, props, {
    __css: {
      variant: 'eui_header.center',
      flexDirection: type === 1 ? setBreakpoint(bp, ['row', 'column']) : null,
      maxWidth: maxWidth
    }
  }), type === 1 ? React.createElement(React.Fragment, null, React.createElement(Flex, null, React.createElement(Logo, null, logo)), React.createElement(Flex, {
    sx: {
      alignItems: 'center'
    }
  }, React.createElement(Menu, {
    menuItems: menu
  }), renderBlock())) : React.createElement(React.Fragment, null, React.createElement(Menu, {
    menuItems: menu.slice(0, mid)
  }), React.createElement(Logo, null, logo), React.createElement(Menu, {
    menuItems: menu.slice(mid)
  }), renderBlock()));
});

function objectWithoutProperties$6(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Reverse = React.forwardRef(function (ref$1, ref) {
  var logo = ref$1.logo;
  if (logo === void 0) logo = 'Logo';
  var menu = ref$1.menu;
  var widgetArea = ref$1.widgetArea;
  var menuToggle = ref$1.menuToggle;
  var colorToggle = ref$1.colorToggle;
  var maxWidth = ref$1.maxWidth;
  if (maxWidth === void 0) maxWidth = 'maxWidth_header';
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'navbar';
  var rest = objectWithoutProperties$6(ref$1, ["logo", "menu", "widgetArea", "menuToggle", "colorToggle", "maxWidth", "variant"]);
  var props = rest;
  return React.createElement(Flex, Object.assign({}, {
    ref: ref,
    variant: variant
  }, props, {
    __css: {
      variant: 'eui_header.columns',
      maxWidth: maxWidth
    }
  }), React.createElement(Flex, {
    className: "col-1"
  }, React.createElement(Menu, {
    menuItems: menu
  }), React.createElement(MenuButton, {
    custom: menuToggle
  })), React.createElement(Flex, {
    className: "col-2"
  }, React.createElement(Logo, null, logo)), React.createElement(Flex, {
    className: "col-3"
  }, React.createElement(WidgetArea, {
    custom: widgetArea
  }), React.createElement(ColorButton, {
    custom: colorToggle
  })));
});

function objectWithoutProperties$7(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Minimal = React.forwardRef(function (ref$1, ref) {
  var logo = ref$1.logo;
  if (logo === void 0) logo = 'Logo';
  var widgetArea = ref$1.widgetArea;
  var menuToggle = ref$1.menuToggle;
  var colorToggle = ref$1.colorToggle;
  var maxWidth = ref$1.maxWidth;
  if (maxWidth === void 0) maxWidth = 'maxWidth_header';
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'navbar';
  var type = ref$1.type;
  var rest = objectWithoutProperties$7(ref$1, ["logo", "widgetArea", "menuToggle", "colorToggle", "maxWidth", "variant", "type"]);
  var props = rest;
  return React.createElement(Flex, Object.assign({}, {
    ref: ref,
    variant: variant
  }, props, {
    __css: {
      variant: type === 3 ? 'eui_header.columns' : 'eui_header.default',
      maxWidth: maxWidth
    }
  }), type === 1 ? React.createElement(React.Fragment, null, React.createElement(Logo, null, logo), React.createElement(Flex, {
    sx: {
      alignItems: 'center'
    }
  }, React.createElement(WidgetArea, {
    custom: widgetArea
  }), React.createElement(MenuButton, {
    custom: menuToggle,
    visibleOnDesktop: true
  }), React.createElement(ColorButton, {
    custom: colorToggle
  }))) : type === 2 ? React.createElement(React.Fragment, null, React.createElement(Flex, {
    sx: {
      alignItems: 'center'
    }
  }, React.createElement(MenuButton, {
    custom: menuToggle,
    visibleOnDesktop: true
  }), React.createElement(Logo, null, logo)), React.createElement(Flex, {
    sx: {
      alignItems: 'center'
    }
  }, React.createElement(WidgetArea, {
    custom: widgetArea
  }), React.createElement(ColorButton, {
    custom: colorToggle
  }))) : React.createElement(React.Fragment, null, React.createElement(Flex, {
    className: "col-1"
  }, React.createElement(MenuButton, {
    custom: menuToggle,
    visibleOnDesktop: true
  })), React.createElement(Flex, {
    className: "col-2"
  }, React.createElement(Logo, null, logo)), React.createElement(Flex, {
    className: "col-3"
  }, React.createElement(WidgetArea, {
    custom: widgetArea
  }), React.createElement(ColorButton, {
    custom: colorToggle
  }))));
});

function objectWithoutProperties$8(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Navbar = React.memo(function (ref) {
  var type = ref.type;
  var rest = objectWithoutProperties$8(ref, ["type"]);
  var props = rest;
  var ref$1 = useOptions();
  var navigation = ref$1.navigation;
  var header = ref$1.header;
  var setOptions = useOptionUpdater();

  if (type !== undefined && type !== navigation) {
    setOptions({
      navigation: type
    });
  }

  switch (navigation) {
    case 'center':
      return React.createElement(Center, Object.assign({}, {
        type: 1
      }, props, {
        bp: header.breakIndex
      }));

    case 'split':
      return React.createElement(Center, Object.assign({}, {
        type: 2
      }, props, {
        bp: header.breakIndex
      }));

    case 'reverse':
      return React.createElement(Reverse, props);

    case 'minimal':
      return React.createElement(Minimal, Object.assign({}, {
        type: 1
      }, props));

    case 'minimal-left':
      return React.createElement(Minimal, Object.assign({}, {
        type: 2
      }, props));

    case 'minimal-center':
      return React.createElement(Minimal, Object.assign({}, {
        type: 3
      }, props));

    case 'basic-left':
      return React.createElement(Basic, Object.assign({}, {
        type: 2
      }, props, {
        bp: header.breakIndex
      }));

    default:
      return React.createElement(Basic, props);
  }
});

function objectWithoutProperties$9(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var AccordionMenu = React.forwardRef(function (ref$1, ref) {
  var menu = ref$1.menu;
  if (menu === void 0) menu = [];
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'accordion-menu';
  var menuType = ref$1.menuType;
  var pathname = ref$1.pathname;
  var rest = objectWithoutProperties$9(ref$1, ["menu", "variant", "menuType", "pathname"]);
  var props = rest;
  var ref$2 = useOptions();
  var mobileMenu = ref$2.mobileMenu;
  var sideNav = ref$2.sideNav;
  var ref$3 = useMenu();
  var showMenu = ref$3[0];
  var toggleMenu = ref$3[1];
  var ref$4 = useSideNav();
  var showSideNav = ref$4[0];
  var toggleSideNav = ref$4[1];

  var getControls = function getControls() {
    if (menuType === 'mobile' && mobileMenu.closeOnRouteChange) {
      return {
        onClick: function onClick(e) {
          return toggleMenu(!showMenu);
        }
      };
    }

    if (menuType === 'sideNav' && sideNav.closeOnRouteChange) {
      return {
        onClick: function onClick(e) {
          return toggleSideNav(!showSideNav);
        }
      };
    }

    return undefined;
  };

  return React.createElement(Box, Object.assign({}, {
    ref: ref,
    as: "ul",
    variant: variant,
    className: "accordion-menu"
  }, props), menu.map(function (item, index) {
    return React.createElement(MenuItem, {
      key: index,
      data: item,
      menuControls: getControls(),
      pathname: pathname
    });
  }));
});
var fullWidth = ['fade', 'fade-up', 'fade-down'];

var getTransition = function getTransition(active, type, width) {
  var opacity = type.includes('fade') ? active ? 1 : 0 : 1;
  var visibility = active ? 'visible' : 'hidden';
  var directionX = type.includes('right') ? {
    right: 0,
    width: width,
    transform: active ? null : 'translateX(100%)'
  } : {
    left: 0,
    width: width,
    transform: active ? null : 'translateX(-100%)'
  };

  var directionY = function directionY() {
    if (type !== 'fade') {
      var sign = type === 'fade-up' ? '' : '-';
      return {
        transform: !active ? "translateY(" + sign + "20px)" : null
      };
    }

    return null;
  };

  var size = fullWidth.includes(type) ? Object.assign({}, {
    width: '100%',
    left: 0
  }, directionY()) : directionX;
  return Object.assign({}, {
    opacity: opacity,
    visibility: visibility
  }, size);
};

var MobileMenu = React.forwardRef(function (props, ref) {
  var ref$1 = useMenu();
  var show = ref$1[0];
  var toggleMenu = ref$1[1];
  var ref$2 = useOptions();
  var mobileMenu = ref$2.mobileMenu;
  var bg = props.bg;
  if (bg === void 0) bg = 'bg_mobileMenu';
  var variant = props.variant;
  if (variant === void 0) variant = 'mobileMenu';
  var width = props.width;
  if (width === void 0) width = mobileMenu.width;
  var transition = props.transition;
  if (transition === void 0) transition = mobileMenu.transition;
  var menu = props.menu;
  if (menu === void 0) menu = [];
  var pathname = props.pathname;
  var children = props.children;
  return React.createElement(React.Fragment, null, mobileMenu.closeOnBlur && !fullWidth.includes(transition) && React.createElement(Overlay, {
    show: show,
    toggle: toggleMenu
  }), React.createElement(Box, Object.assign({}, {
    ref: ref,
    id: "mobile-menu",
    variant: variant
  }, props, {
    __css: Object.assign({}, {
      position: 'fixed',
      bg: bg,
      top: 0,
      bottom: 0,
      zIndex: 100,
      willChange: 'transform opacity',
      transition: 'all ease .3s'
    }, getTransition(show, transition, width))
  }), mobileMenu.defaultCloseButton ? React.createElement(MenuButton, {
    sx: {
      position: 'absolute',
      top: 10,
      right: 10,
      zIndex: 1
    },
    closeIcon: true
  }) : null, children || React.createElement(AccordionMenu, {
    menu: menu,
    menuType: "mobile",
    pathname: pathname
  })));
});

function objectWithoutProperties$a(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var format = function format(value) {
  return isNaN(value) ? value : value + "px";
}; // TODO - Figure out padding top vs Main


var Content = React.forwardRef(function (ref$1, ref) {
  var layout = ref$1.layout;
  var rest = objectWithoutProperties$a(ref$1, ["layout"]);
  var props = rest;
  var ref$2 = useOptions();
  var content = ref$2.content;
  var ref$3 = useLayout();
  var baseLayout = ref$3[0];
  var setLayout = ref$3[1];

  if (layout !== undefined && layout !== baseLayout) {
    setLayout(layout);
  }

  var sidebarPartial = baseLayout === 'sidebar-content' ? {
    gridTemplateColumns: function gridTemplateColumns(t) {
      return setBreakpoint(content.breakIndex, ["1fr", format(t.sizes.width_sidebar) + " 1fr"]);
    }
  } : baseLayout === 'content-sidebar' ? {
    gridTemplateColumns: function gridTemplateColumns(t) {
      return setBreakpoint(content.breakIndex, ["1fr", "1fr " + format(t.sizes.width_sidebar)]);
    }
  } : null;
  return React.createElement(Box, Object.assign({}, {
    ref: ref,
    id: "site-inner"
  }, props, {
    __css: Object.assign({}, {
      variant: "eui_layout." + baseLayout
    }, sidebarPartial, {
      minHeight: '80vh'
    })
  }));
});

function objectWithoutProperties$b(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Main = React.forwardRef(function (ref$1, ref) {
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'main';
  var rest = objectWithoutProperties$b(ref$1, ["variant"]);
  var props = rest;
  return React.createElement(Box, Object.assign({}, {
    ref: ref,
    as: "main",
    id: "content",
    role: "main",
    variant: variant
  }, props, {
    __css: {
      flex: 1
    }
  }));
});

function objectWithoutProperties$c(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var format$1 = function format$1(value) {
  return isNaN(value) ? value : value + "px";
};

var SideNav = React.forwardRef(function (ref$1, ref) {
  var bg = ref$1.bg;
  if (bg === void 0) bg = 'bg_sideNav';
  var toggleVariant = ref$1.toggleVariant;
  var customToggle = ref$1.customToggle;
  if (customToggle === void 0) customToggle = 'Toggle';
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'sideNav';
  var menu = ref$1.menu;
  var pathname = ref$1.pathname;
  var children = ref$1.children;
  var rest = objectWithoutProperties$c(ref$1, ["bg", "toggleVariant", "customToggle", "variant", "menu", "pathname", "children"]);
  var props = rest;
  var ref$2 = useSideNav();
  var active = ref$2[0];
  var setActive = ref$2[1];
  var ref$3 = useOptions();
  var layout = ref$3.layout;
  var sideNav = ref$3.sideNav;
  var bp = sideNav.breakIndex;

  var getTransform = function getTransform(width) {
    var w = Array.isArray(width) ? width[bp] : width;
    var shift = layout === 'sidenav-content' ? -w : w;
    return active ? "translateX(0)" : "translateX(" + format$1(shift) + ")";
  };

  return React.createElement(React.Fragment, null, sideNav.closeOnBlur && React.createElement(Overlay, {
    show: active,
    toggle: setActive,
    type: "sideNav",
    bp: sideNav.breakIndex
  }), React.createElement(Box, Object.assign({}, {
    ref: ref,
    variant: variant,
    id: "side-nav",
    role: "navigation"
  }, props, {
    __css: {
      bg: bg,
      position: setBreakpoint(bp, ['fixed', 'relative']),
      top: 0,
      bottom: 0,
      zIndex: setBreakpoint(bp, [100, 0]),
      width: function width(t) {
        return t.sizes.width_sideNav;
      },
      willChange: 'transform',
      transform: function transform(t) {
        return setBreakpoint(bp, [getTransform(t.sizes.width_sideNav), 'none']);
      },
      transition: 'transform ease .3s'
    }
  }), children || React.createElement(AccordionMenu, {
    menu: menu,
    menuType: "sideNav",
    pathname: pathname
  })), sideNav.floatingToggle ? React.createElement(Box, {
    as: "button",
    id: "toggle-sidenav",
    title: "Toggle SideNav",
    'aria-label': "Toggle side navigation",
    onClick: setActive,
    variant: toggleVariant,
    sx: {
      position: 'fixed',
      display: setBreakpoint(bp, ['inline-block', 'none']),
      bottom: 30,
      zIndex: 100
    }
  }, customToggle) : null);
});
var Sidebar = React.forwardRef(function (props, ref) {
  return React.createElement(Box, Object.assign({}, {
    ref: ref,
    as: props.as || 'aside',
    id: "primary-sidebar",
    role: "complementary"
  }, props));
});

function objectWithoutProperties$d(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Footer = React.forwardRef(function (ref$1, ref) {
  var bg = ref$1.bg;
  if (bg === void 0) bg = 'bg_footer';
  var maxWidth = ref$1.maxWidth;
  if (maxWidth === void 0) maxWidth = 'maxWidth_footer';
  var variant = ref$1.variant;
  if (variant === void 0) variant = 'footer';
  var children = ref$1.children;
  var rest = objectWithoutProperties$d(ref$1, ["bg", "maxWidth", "variant", "children"]);
  var props = rest;
  return React.createElement(Box, Object.assign({}, {
    ref: ref,
    as: "footer",
    id: "footer",
    role: "contentinfo",
    variant: variant,
    bg: bg
  }, props), React.createElement(Flex, {
    __css: {
      maxWidth: maxWidth,
      mx: 'auto'
    }
  }, children));
});

function objectWithoutProperties$e(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var Section = React.forwardRef(function (ref$1, ref) {
  var maxWidth = ref$1.maxWidth;
  if (maxWidth === void 0) maxWidth = 'maxWidth_section';
  var bg = ref$1.bg;
  var background = ref$1.background;
  var children = ref$1.children;
  var rest = objectWithoutProperties$e(ref$1, ["maxWidth", "bg", "background", "children"]);
  var props = rest;
  return React.createElement(Box, {
    ref: ref,
    as: props.as || 'section',
    bg: bg,
    sx: {
      background: background,
      width: '100%'
    }
  }, React.createElement(Box, Object.assign({}, props, {
    __css: {
      maxWidth: maxWidth,
      mx: 'auto'
    }
  }), children));
});

var SiteInner = function SiteInner(ref) {
  var sideNav = ref.sideNav;
  var sidebar = ref.sidebar;
  var menu = ref.menu;
  var children = ref.children;
  var ref$1 = useOptions();
  var layout = ref$1.layout;

  switch (layout) {
    case 'content-sidebar':
      return React.createElement(Content, null, React.createElement(Main, null, children), React.createElement(Sidebar, null, sidebar));

    case 'sidebar-content':
      return React.createElement(Content, null, React.createElement(Sidebar, null, sidebar), React.createElement(Main, null, children));

    case 'content-sideNav':
      return React.createElement(Content, null, React.createElement(Main, null, children), React.createElement(SideNav, {
        menu: menu,
        customToggle: sideNav[1],
        pathname: sideNav[2]
      }, sideNav[0]));

    case 'sideNav-content':
      return React.createElement(Content, null, React.createElement(SideNav, {
        menu: menu,
        customToggle: sideNav[1],
        pathname: sideNav[2]
      }, sideNav[0]), React.createElement(Main, null, children));

    default:
      return React.createElement(Content, null, React.createElement(Main, null, children));
  }
};

var Template$$1 = function Template$$1(ref) {
  var theme = ref.theme;
  var options = ref.options;
  var components = ref.components;
  var topbar = ref.topbar;
  var headerWidgets = ref.headerWidgets;
  var menuToggle = ref.menuToggle;
  var colorToggle = ref.colorToggle;
  var logo = ref.logo;
  var menu = ref.menu;
  var mobileMenu = ref.mobileMenu;
  if (mobileMenu === void 0) mobileMenu = 'default';
  var sideNav = ref.sideNav;
  var sideNavToggle = ref.sideNavToggle;
  var sidebar = ref.sidebar;
  var footer = ref.footer;
  var pathname = ref.pathname;
  var children = ref.children;
  return React.createElement(Layout, {
    theme: theme,
    options: options,
    components: components
  }, topbar && React.createElement(Topbar, null, topbar), React.createElement(Header, null, React.createElement(Navbar, {
    logo: logo,
    menu: menu,
    widgetArea: headerWidgets,
    menuToggle: menuToggle,
    colorToggle: colorToggle,
    pathname: pathname
  }), mobileMenu === 'default' ? React.createElement(MobileMenu, {
    menu: menu
  }) : React.isValidElement(mobileMenu) ? React.createElement(MobileMenu, null, mobileMenu) : null), React.createElement(SiteInner, {
    sideNav: [sideNav, sideNavToggle, pathname],
    sidebar: sidebar,
    menu: menu
  }, children), footer && React.createElement(Footer, null, footer));
};

function useMakerUI() {
  var ref = useExtensions();
  var state = ref[0];
  var setState = ref[1];
  var ref$1 = useThemeUI();
  var theme = ref$1.theme;
  var colorMode = ref$1.colorMode;
  var setColorMode = ref$1.setColorMode;
  var options = useOptions();

  function extendTheme(newTheme, key) {
    // Check if the component's key has already been added before updating context / causing a re-render
    if (key && state.themeKeys.includes(key)) {
      return;
    }

    return setState(function (s) {
      return Object.assign({}, s, {
        extendedTheme: cjs(s.extendedTheme, validate(newTheme))
      });
    });
  }

  function extendOptions(newOptions, key) {
    if (key && state.themeKeys.includes(key)) {
      return;
    }

    return setState(function (s) {
      return Object.assign({}, s, {
        extendedOptions: cjs(s.extendedOptions, newOptions)
      });
    });
  }

  return {
    theme: theme,
    colorMode: colorMode,
    setColorMode: setColorMode,
    extendTheme: extendTheme,
    options: options,
    extendOptions: extendOptions
  };
}

export { useOptions, useMenu, useSideNav, setBreakpoint, generateId, Layout, Topbar, Header, Navbar, MobileMenu, Content, Main, SideNav, Sidebar, Footer, Section, Template$$1 as Template, AccordionMenu, useMakerUI };